import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { UserCircle, Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../store/auth'
import { authApi } from '../../api/auth'

/**
 * Admin → Profile. The current admin's editable account.
 * - Edits firstName / lastName / email via PATCH /auth/me.
 * - Role + companyId are not user-managed; rendered read-only as context.
 * - Password change lives on a separate flow (sign out → forgot password)
 *   intentionally — admins shouldn't have a quick password-change form
 *   that an unattended session could exploit.
 */
export function ProfilePage() {
  useEffect(() => { document.title = 'Profile' }, [])
  const { user, setAuth } = useAuthStore()
  const accessToken = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null

  const { register, handleSubmit, reset, formState: { isDirty, errors } } = useForm<{
    firstName: string; lastName: string
  }>()

  // Hydrate the form whenever the auth store user updates.
  useEffect(() => {
    if (user) reset({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
    })
  }, [user, reset])

  const mutation = useMutation({
    mutationFn: (dto: { firstName: string; lastName: string }) =>
      authApi.updateMe(dto).then(r => r.data),
    onSuccess: (updated) => {
      // Sync the auth store so the sidebar avatar/name update without a refresh.
      if (accessToken) setAuth(updated, accessToken)
      toast.success('Profile updated')
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to update profile')
    },
  })

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded bg-primary-50 text-primary-600 flex items-center justify-center">
          <UserCircle className="w-5 h-5" />
        </div>
        <div>
          <h1 className="page-title">Profile</h1>
          <p className="text-xs text-gray-500 mt-0.5">Edit your own account details.</p>
        </div>
      </div>

      <div className="card p-5 space-y-4">
        <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">First Name *</label>
              <input
                {...register('firstName', { required: 'First name is required' })}
                className="input"
              />
              {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="label">Last Name *</label>
              <input
                {...register('lastName', { required: 'Last name is required' })}
                className="input"
              />
              {errors.lastName && <p className="text-xs text-red-600 mt-1">{errors.lastName.message}</p>}
            </div>
          </div>

          <div>
            <label className="label flex items-center gap-1">
              <Lock className="w-3 h-3 text-gray-400" /> Email
            </label>
            <input value={user?.email ?? ''} type="email" disabled readOnly className="input" />
            <p className="text-xs text-gray-500 mt-1">
              Email is also your sign-in identifier and can't be changed here.
              Contact another administrator if you need to change it.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <div>
              <label className="label">Role</label>
              <input value={user?.role ?? ''} disabled readOnly className="input" />
            </div>
            <div>
              <label className="label">Workspace</label>
              <input value={user?.companyId ? 'Tenant-scoped' : 'System (no workspace)'} disabled readOnly className="input" />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={!isDirty || mutation.isPending}
              className="btn btn-primary btn-sm"
            >
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>

      <div className="card p-5 mt-4 space-y-2 text-sm">
        <p className="font-medium text-gray-700">Password</p>
        <p className="text-xs text-gray-500">
          To change your password, sign out and use the “Forgot your password?”
          link on the sign-in screen. The reset email goes to the address above.
        </p>
      </div>
    </div>
  )
}
